// @ts-strict-ignore

import {CountType, InsightsCountType, RetentionCountType} from 'common/query-builder/query-entry-section/types';
import {FunnelsCountType} from 'common/report/funnels/models/constants';
import {AggregationGroupLabel, QueryOrigin} from 'common/query-builder/types/shared-types';
import {ALL_MATH_TYPES} from 'common/report/constants';
import {
  COUNT_USERS_ONCE_TYPE,
  FUNNELS_MATH_TYPES,
  MATH_TYPE,
  MetricType,
  NUMERIC_MATH_TYPES,
  PER_USER_AGGREGATIONS,
  PRIMITIVE_MATH_TYPES,
  RETENTION_MATH_TYPES,
  XAU_MATH_TYPES,
} from 'common/types/reports/bookmark';
import {Segmentation} from 'common/types/reports/query';
import {Translatable} from 'common/i18n';
import {isXauMathType} from 'common/query-builder/query-builder/util';
import {SectionItem} from 'common/components/items-menu/types';

import {isListOfObjectsSegment} from '../segment-menu/types';
import {PropertySectionItem, SectionItemProperty} from '../properties-menu/item-menu-types';
import {propertyItemToSegmentation} from '../properties-menu/util';
import {FUNNELS_TIME_TO_CONVERSION, RECURRING_REVENUE_CHANGE} from '../properties-menu/constants';
import {isProperty} from '../properties-menu/types';
import {openCustomPercentileModal} from './openCustomPercentileModal';

/** Modal trigger function type for count menu aggregation selectors */
export type ModalResolvedValue = {
  aggregation: SectionItem<AggregationSelector>;
  subSelectionIndex: number;
} | null;

export type CountMenuAdditionalModalTrigger = (params: ModalResolvedValue) => Promise<ModalResolvedValue | null>;

/** Modal component type for count menu aggregation selectors */
const learnMore =
  (url: string): Translatable =>
  ({t}) =>
    `\n[${t(`common.learn-more`)}](${url})`;

const HELP_DOCS_INSIGHTS_LINK = learnMore(`https://docs.mixpanel.com/docs/analysis/reports/insights#measurements`);
const HELP_DOCS_RETENTION_LINK = learnMore(`https://docs.mixpanel.com/docs/reports/retention`);
const HELP_DOCS_FUNNELS_LINK = learnMore(`https://docs.mixpanel.com/docs/reports/funnels#counting-method`);
const HELP_DOCS_FUNNELS_TTC_NEW_LINK = learnMore(`https://docs.mixpanel.com/docs/reports/funnels#time-to-convert`);
const HELP_DOCS_FUNNELS_PROPERTY_SUM_LINK = learnMore(`https://docs.mixpanel.com/docs/reports/funnels#property-sum`);

/** List of selections derived from selected count type */
export enum SubSelectionTypes {
  Property = `property`,
  NestedProperty = `nestedProperty`,
  RevenueProperty = `revenueProperty`,
  AggregationPerUser = `aggregationPerUser`,
  Aggregation = `aggregation`,
  CountUsersOnce = `countUsersOnceType`,
}

export type GroupLabelParams = Omit<
  {[key in AggregationGroupLabel]: Translatable},
  AggregationGroupLabel.MetricType | AggregationGroupLabel.QueryOrigin
> & {
  [AggregationGroupLabel.MetricType]: MetricType | null;
  [AggregationGroupLabel.QueryOrigin]: QueryOrigin;
};

export type SelectorLabel = (params: GroupLabelParams & {percentile?: number}) => Translatable;

export type RequiredSelectionsParams = {
  [SubSelectionTypes.Property]: PropertySectionItem;
  [SubSelectionTypes.NestedProperty]: Segmentation;
  [SubSelectionTypes.Aggregation]: MATH_TYPE;
  [SubSelectionTypes.AggregationPerUser]: MATH_TYPE;
  [SubSelectionTypes.CountUsersOnce]: COUNT_USERS_ONCE_TYPE;
};

/**
 * Definition of count type in count menu
 * i.e. Uniques, Total Events, Property per Unique, etc.
 */
export type CountTypeSelector = {
  countType: CountType;

  label: SelectorLabel;
  shortLabel?: SelectorLabel;
  description?: SelectorLabel;

  /** Defines across-users aggregations */
  operator?: MATH_TYPE;
  /** Defines per-user aggregations */
  perUserOperator?: PER_USER_AGGREGATIONS;
  /** Defines how to count unique users across segments */
  countUsersOnceType?: COUNT_USERS_ONCE_TYPE;

  /** 1st level aggregation definitions for the given count type */
  aggregations?: AggregationSelector[];
  /** Specifies if the aggregation acts on a property */
  forProperty?: boolean;
  property?: PropertySectionItem;

  expanderIcon?: string;
  /** List of selections required to be able to commit */
  requiredSubSelections?: (({property}: {property: PropertySectionItem}) => SubSelectionTypes[]) | SubSelectionTypes[];
  /** Specifies if the given count type can be committed */
  hasRequiredSelections: (param: RequiredSelectionsParams) => boolean;
  /** Specifies if the sub selections can be expanded */
  canExpandSubSelections: ({
    countType,
    aggregation,
    countUsersOnceType,
  }: {
    countType: CountType;
    aggregation: MATH_TYPE;
    countUsersOnceType?: COUNT_USERS_ONCE_TYPE;
  }) => boolean;
};

/**
 * Definition of aggregation that acts on count type
 * i.e. Sum, Average, Median, etc.
 */
export type AggregationSelector = {
  label: SelectorLabel;
  shortLabel?: SelectorLabel;
  description?: SelectorLabel;

  /** Defines across-users aggregations */
  operator?: MATH_TYPE;
  /** Defines per-user aggregations */
  perUserOperator?: PER_USER_AGGREGATIONS;
  /** Defines how to count unique users across segments */
  countUsersOnceType?: COUNT_USERS_ONCE_TYPE;

  /**
   * 2nd level aggregation definitions for the 1st pass aggregation
   * Defines a list of aggregations associated with the given aggregation
   * e.g. Median (2nd level aggregation) Sum (1st pass aggregation) of Price per Unique
   */
  subAggregations?: AggregationSelector[];
  /**
   * Defines aggregations to be displayed in a nested menu
   * e.g. P25, P75, P90 for Percentile
   */
  subSections?: AggregationSelector[];
  /**
   * Modal trigger function to call when this aggregation requires additional user input.
   * If provided, the function will be called instead of immediately selecting the aggregation.
   */
  requiredModalTrigger?: CountMenuAdditionalModalTrigger;
  percentile?: number;
};

/**
 * Template variables for output copies for the selected count type
 * @example: aggregationOperator: total|average|median
 * @example: aggregationOperatorPerUser: total|average|median
 * @example: propertyName: Price|Revenue
 */
export enum CountSelections {
  AggregationOperator = `aggregationOperator`,
  AggregationOperatorPerUser = `aggregationOperatorPerUser`,
  Percentile = `percentile`,
  PropertyName = `propertyName`,
  PropertyValue = `propertyValue`,
  CountUsersOnceType = `countUsersOnceType`,
}

export type CountLabelGeneratorParams = Omit<
  {[key in CountSelections]: string | null | undefined},
  CountSelections.PropertyName | CountSelections.Percentile
> & {
  [CountSelections.PropertyName]: Translatable;
  /** When true, use simplified labels without data group references (for metric-level data group support) */
  withDataGroupSelect?: boolean;

  /** when set, generates count labels for metric names */
  forMetricName?: boolean;
} & {
  [CountSelections.Percentile]?: number;
} & GroupLabelParams;

export type CountLabelGenerator = (params: CountLabelGeneratorParams) => Translatable;

export const OperatorToString: Record<any, Translatable> = {
  [ALL_MATH_TYPES.TOTAL]: ({t}) => t(`query-builder.count-menu.operators.sum`),
  [ALL_MATH_TYPES.AVERAGE]: ({t}) => t(`query-builder.count-menu.operators.avg`),
  [ALL_MATH_TYPES.MEDIAN]: ({t}) => t(`query-builder.count-menu.operators.median`),
  [ALL_MATH_TYPES.MIN]: ({t}) => t(`query-builder.count-menu.operators.min`),
  [ALL_MATH_TYPES.MAX]: ({t}) => t(`query-builder.count-menu.operators.max`),
  [ALL_MATH_TYPES.P25]: `P25`,
  [ALL_MATH_TYPES.P75]: `P75`,
  [ALL_MATH_TYPES.P90]: `P90`,
  [ALL_MATH_TYPES.P99]: `P99`,
  [ALL_MATH_TYPES.UNIQUE_VALUES]: ({t}) => t(`query-builder.count-menu.operators.distinct`),
  [ALL_MATH_TYPES.HISTOGRAM]: ({t}) => t(`query-builder.count-menu.operators.distribution`),
  [ALL_MATH_TYPES.UNIQUE]: ({t}) => t(`query-builder.count-menu.operators.unique`),
  [ALL_MATH_TYPES.DAU]: ({t}) => t(`query-builder.count-menu.operators.dau`),
  [ALL_MATH_TYPES.WAU]: ({t}) => t(`query-builder.count-menu.operators.wau`),
  [ALL_MATH_TYPES.MAU]: ({t}) => t(`query-builder.count-menu.operators.mau`),
  [ALL_MATH_TYPES.CUMULATIVE_UNIQUE]: ({t}) => t(`query-builder.count-menu.operators.cumulative-users`),
  [FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE]: ({t}) => t(`query-builder.count-menu.conversion-rate-uniques`),
  [FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL]: ({t}) => t(`query-builder.count-menu.conversion-rate-total`),
  [FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION]: ({t}) => t(`query-builder.count-menu.conversion-rate-sessions`),
};

const OperatorToStringWithData: Record<any, (data?: {percentile?: number}) => Translatable> = {
  [ALL_MATH_TYPES.CUSTOM_PERCENTILE]:
    (data) =>
    ({t}) =>
      data?.percentile ? `P${data.percentile}` : t(`query-builder.count-menu.operators.custom-percentile`),
};

export function getOperatorLabel(operator: string, percentile?: number) {
  if (operator in OperatorToString) {
    return OperatorToString[operator];
  } else if (operator in OperatorToStringWithData) {
    return OperatorToStringWithData[operator]({percentile});
  }
  return null;
}

export const COUNT_USERS_ONCE_OPTIONS = {
  [COUNT_USERS_ONCE_TYPE.MULTIPLE]: {
    label: ({defaultType}: {defaultType: COUNT_USERS_ONCE_TYPE}) => {
      return ({t}) =>
        `${t(`query-builder.count-menu.count-users-once.every-segment`)}${defaultType === COUNT_USERS_ONCE_TYPE.MULTIPLE ? ` (${t(`query-builder.default`)})` : ``}`;
    },
    selectLabel:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.count-users-once.count-every-segment`),
    description:
      ({timePeriod}) =>
      ({t}) =>
        t(`query-builder.count-menu.count-users-once.every-segment-tooltip`, {timePeriod}),
    enabledStates: [`enabled`, `disabled`],
  },
  [COUNT_USERS_ONCE_TYPE.LAST]: {
    label: ({defaultType}: {defaultType: COUNT_USERS_ONCE_TYPE}) => {
      return ({t}) =>
        `${t(`query-builder.count-menu.count-users-once.last-segment`)}${defaultType === COUNT_USERS_ONCE_TYPE.LAST ? ` (${t(`query-builder.default`)})` : ``}`;
    },
    selectLabel:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.count-users-once.count-last-segment`),
    description:
      ({timePeriod}) =>
      ({t}) =>
        t(`query-builder.count-menu.count-users-once.last-segment-tooltip`, {timePeriod}),
    enabledStates: [`enabled`, `required`],
  },
  [COUNT_USERS_ONCE_TYPE.FIRST]: {
    label: ({defaultType}: {defaultType: COUNT_USERS_ONCE_TYPE}) => {
      return ({t}) =>
        `${t(`query-builder.count-menu.count-users-once.first-segment`)}${defaultType === COUNT_USERS_ONCE_TYPE.FIRST ? ` (${t(`query-builder.default`)})` : ``}`;
    },
    selectLabel:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.count-users-once.count-first-segment`),
    description:
      ({timePeriod}) =>
      ({t}) =>
        t(`query-builder.count-menu.count-users-once.first-segment-tooltip`, {timePeriod}),
    enabledStates: [`enabled`, `required`],
  },
};

const InsightsProfileCountLabelGeneratorMappings = new Map<InsightsCountType, CountLabelGenerator>([
  [
    InsightsCountType.Total,
    () =>
      ({t}) =>
        t(`query-builder.count-menu.total-profiles`),
  ],
  [
    InsightsCountType.UniqueUsers,
    () =>
      ({t}) =>
        t(`query-builder.count-menu.total-profiles`),
  ],
  [
    InsightsCountType.PropertyValues,
    ({aggregationOperator, propertyName}: CountLabelGeneratorParams) => {
      return ({t, print}) =>
        t(`query-builder.count-menu.agg-of-prop`, {
          aggregationOperator: print(OperatorToString[aggregationOperator]),
          propertyName: print(propertyName),
        });
    },
  ],
]);

export const DefaultCountUsersOnceType = new Map<MetricType, COUNT_USERS_ONCE_TYPE>([
  [MetricType.Retention, COUNT_USERS_ONCE_TYPE.FIRST],
  [MetricType.RetentionFrequency, COUNT_USERS_ONCE_TYPE.FIRST],
  [MetricType.Funnel, COUNT_USERS_ONCE_TYPE.MULTIPLE],
  [MetricType.Event, COUNT_USERS_ONCE_TYPE.MULTIPLE],
  [MetricType.Simple, COUNT_USERS_ONCE_TYPE.MULTIPLE],
  [MetricType.CustomEvent, COUNT_USERS_ONCE_TYPE.MULTIPLE],
]);

const InsightsCountLabelGeneratorMappings = new Map<InsightsCountType, CountLabelGenerator>([
  [
    InsightsCountType.UniqueUsers,
    ({aggregationOperator, countUsersOnceType, pluralGroupTitle, metricType, withDataGroupSelect, forMetricName}) => {
      const isXau = isXauMathType(aggregationOperator as MATH_TYPE);
      const isCumulativeUnique = PRIMITIVE_MATH_TYPES.CUMULATIVE_UNIQUE === aggregationOperator;

      const operatorLabel = (isXau && aggregationOperator.toUpperCase()) || (isCumulativeUnique && `Cumulative Users`);

      const defaultType: COUNT_USERS_ONCE_TYPE = DefaultCountUsersOnceType.get(metricType as MetricType);
      const countUsersLabel = COUNT_USERS_ONCE_OPTIONS[countUsersOnceType]?.label({defaultType});

      if (forMetricName) {
        // yields a "WAU of Add to Cart" metric name, or "Uniques of Add to Cart"
        return isXau ? operatorLabel : ({t}) => t(`common.uniques`);
      }

      return ({t, print}) => {
        const baseLabel = withDataGroupSelect
          ? t(`common.uniques`)
          : t(`query-builder.count-menu.unique-dg`, {dataGroupName: print(pluralGroupTitle)});

        return [baseLabel, operatorLabel, print(countUsersLabel)].filter(Boolean).join(` - `);
      };
    },
  ],
  [
    InsightsCountType.Total,
    () =>
      ({t}) =>
        t(`query-builder.count-menu.total-events`),
  ],
  [
    InsightsCountType.TotalSessions,
    () =>
      ({t}) =>
        t(`query-builder.count-menu.total-sessions`),
  ],
  [
    InsightsCountType.FrequencyPerUser,
    ({aggregationOperator, percentile, groupTitle, withDataGroupSelect}: CountLabelGeneratorParams) => {
      return ({t, print}) => {
        const dataGroupName = print(groupTitle);
        const printedAggOperator = print(getOperatorLabel(aggregationOperator, percentile));

        if (aggregationOperator === ALL_MATH_TYPES.HISTOGRAM) {
          return withDataGroupSelect
            ? t(`query-builder.count-menu.freq-dist`)
            : t(`query-builder.count-menu.freq-dist-per-group`, {dataGroupName});
        }
        return withDataGroupSelect
          ? t(`query-builder.count-menu.agg-freq`, {aggregationOperator: printedAggOperator})
          : t(`query-builder.count-menu.agg-freq-per-group`, {
              dataGroupName,
              aggregationOperator: printedAggOperator,
            });
      };
    },
  ],
  [
    InsightsCountType.PropertyValues,
    ({aggregationOperator, percentile, propertyName}: CountLabelGeneratorParams) => {
      return ({t, print}) =>
        t(`query-builder.count-menu.agg-of-prop`, {
          propertyName: print(propertyName),
          aggregationOperator: print(getOperatorLabel(aggregationOperator, percentile)),
        });
    },
  ],
  [
    InsightsCountType.PropertyValuePerUser,
    ({
      aggregationOperator,
      aggregationOperatorPerUser,
      percentile,
      propertyName,
      groupTitle,
      withDataGroupSelect,
    }: CountLabelGeneratorParams) => {
      return ({t, print}) => {
        const key = withDataGroupSelect
          ? `query-builder.count-menu.agg-prop-without-group`
          : `query-builder.count-menu.agg-prop-per-group`;

        const params = {
          aggregationOperator: print(getOperatorLabel(aggregationOperator, percentile)),
          propertyName: print(propertyName),
          aggregationOperatorPerUser: print(getOperatorLabel(aggregationOperatorPerUser, percentile)),
          ...(withDataGroupSelect ? {} : {dataGroupName: print(groupTitle)}),
        };

        return t(key, params);
      };
    },
  ],
]);

const RetentionCountLabelGeneratorMappings = new Map<RetentionCountType, CountLabelGenerator>([
  [
    RetentionCountType.Unique,
    ({countUsersOnceType, pluralGroupTitle, metricType, withDataGroupSelect}) => {
      const defaultType: COUNT_USERS_ONCE_TYPE = DefaultCountUsersOnceType.get(metricType as MetricType);
      const countUsersLabel = COUNT_USERS_ONCE_OPTIONS[countUsersOnceType]?.label({defaultType});

      return ({t, print}) => {
        const baseLabel = withDataGroupSelect
          ? t(`common.uniques`)
          : t(`query-builder.count-menu.unique-dg`, {dataGroupName: print(pluralGroupTitle)});

        return [baseLabel, print(countUsersLabel)].filter(Boolean).join(` - `);
      };
    },
  ],
  [
    RetentionCountType.RetentionRate,
    () =>
      ({t}) =>
        t(`query-builder.count-menu.retention-rate`),
  ],
  [
    RetentionCountType.PropertySum,
    ({propertyName}: CountLabelGeneratorParams) => {
      return ({t, print}) =>
        t(`query-builder.count-menu.agg-of-prop`, {
          aggregationOperator: t(`query-builder.count-menu.operators.sum`),
          propertyName: print(propertyName),
        });
    },
  ],
  [
    RetentionCountType.PropertyAverage,
    ({propertyName}: CountLabelGeneratorParams) => {
      return ({t, print}) =>
        t(`query-builder.count-menu.agg-of-prop`, {
          aggregationOperator: t(`query-builder.count-menu.operators.avg`),
          propertyName: print(propertyName),
        });
    },
  ],
]);

const FunnelsCountLabelGeneratorMappings = new Map<FunnelsCountType, CountLabelGenerator>([
  [
    FunnelsCountType.Unique,
    ({countUsersOnceType, pluralGroupTitle, metricType, withDataGroupSelect}) => {
      const defaultType: COUNT_USERS_ONCE_TYPE = DefaultCountUsersOnceType.get(metricType as MetricType);
      const countUsersLabel = COUNT_USERS_ONCE_OPTIONS[countUsersOnceType]?.label({defaultType});

      return ({t, print}) => {
        const baseLabel = withDataGroupSelect
          ? t(`common.uniques`)
          : t(`query-builder.count-menu.unique-dg`, {dataGroupName: print(pluralGroupTitle)});

        return [baseLabel, print(countUsersLabel)].filter(Boolean).join(` - `);
      };
    },
  ],
  [
    FunnelsCountType.Session,
    () =>
      ({t}) =>
        t(`query-builder.count-menu.total-sessions`),
  ],
  [
    FunnelsCountType.ConversionRate,
    ({aggregationOperator}) => {
      if (FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL === aggregationOperator) {
        return ({t}) => t(`query-builder.count-menu.conversion-rate-total`);
      }
      if (FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION === aggregationOperator) {
        return ({t}) => t(`query-builder.count-menu.conversion-rate-sessions`);
      }
      return ({t}) => t(`query-builder.count-menu.conversion-rate`);
    },
  ],
  [
    FunnelsCountType.PropertySum,
    ({propertyName}: CountLabelGeneratorParams) => {
      return ({t, print}) =>
        t(`query-builder.count-menu.agg-of-prop`, {
          aggregationOperator: t(`query-builder.count-menu.operators.sum`),
          propertyName: print(propertyName),
        });
    },
  ],
  [
    FunnelsCountType.General,
    ({aggregationOperator, percentile, propertyName, propertyValue}: CountLabelGeneratorParams) => {
      if (propertyValue === FUNNELS_TIME_TO_CONVERSION.value) {
        if (aggregationOperator in OperatorToString || aggregationOperator in OperatorToStringWithData) {
          return ({t, print}) =>
            t(`query-builder.count-menu.agg-of-prop`, {
              aggregationOperator: print(getOperatorLabel(aggregationOperator, percentile)),
              propertyName: `TTC`,
            });
        }
        return `TTC`;
      } else if (propertyValue !== null && propertyValue !== undefined) {
        return ({t, print}) =>
          t(`query-builder.count-menu.agg-of-prop`, {
            aggregationOperator: print(getOperatorLabel(aggregationOperator, percentile)),
            propertyName: print(propertyName),
          });
      }
      return ({t}) => t(`query-builder.count-menu.total-conversions`);
    },
  ],
]);

export const CountLabelGeneratorMappings = new Map<MetricType, Map<CountType, CountLabelGenerator>>([
  [MetricType.Event, InsightsCountLabelGeneratorMappings],
  [MetricType.Simple, InsightsCountLabelGeneratorMappings],
  [MetricType.CustomEvent, InsightsCountLabelGeneratorMappings],
  [MetricType.People, InsightsProfileCountLabelGeneratorMappings],
  [MetricType.Cohort, InsightsProfileCountLabelGeneratorMappings],
  [MetricType.Funnel, FunnelsCountLabelGeneratorMappings],
  [MetricType.Retention, RetentionCountLabelGeneratorMappings],
  [MetricType.RetentionFrequency, RetentionCountLabelGeneratorMappings],
]);

export const InsightsCountSubMenuHeader = {
  [InsightsCountType.UniqueUsers]: {
    [SubSelectionTypes.Aggregation]: {
      label:
        () =>
        ({t}) =>
          t(`query-builder.count-menu.sub-menu.agg-by`),
      description:
        ({pluralGroup}) =>
        ({t, print}) =>
          t(`query-builder.count-menu.sub-menu.unique-group-tooltip`, {
            dataGroupName: print(pluralGroup),
          }) + print(HELP_DOCS_INSIGHTS_LINK),
      icon: `info-xs`,
    },
  },
  [InsightsCountType.FrequencyPerUser]: {
    [SubSelectionTypes.Aggregation]: {
      label:
        () =>
        ({t}) =>
          t(`query-builder.count-menu.sub-menu.agg-by`),
      description:
        ({group}) =>
        ({t, print}) =>
          t(`query-builder.count-menu.sub-menu.per-group-tooltip`, {
            dataGroupName: print(group),
          }) + print(HELP_DOCS_INSIGHTS_LINK),
      icon: `info-xs`,
    },
  },
  [InsightsCountType.PropertyValues]: {
    [SubSelectionTypes.Aggregation]: {
      label:
        () =>
        ({t}) =>
          t(`query-builder.count-menu.sub-menu.agg-by`),
      description:
        () =>
        ({t, print}) =>
          t(`query-builder.count-menu.sub-menu.prop-values-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
      icon: `info-xs`,
    },
  },
  [InsightsCountType.PropertyValuePerUser]: {
    [SubSelectionTypes.AggregationPerUser]: {
      label:
        ({groupTitle}) =>
        ({t, print}) =>
          t(`query-builder.count-menu.sub-menu.agg-group-by`, {
            dataGroupName: print(groupTitle),
          }),
      description:
        () =>
        ({t, print}) =>
          t(`query-builder.count-menu.sub-menu.prop-values-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
      icon: `info-xs`,
    },
    [SubSelectionTypes.Aggregation]: {
      label:
        () =>
        ({t}) =>
          t(`query-builder.count-menu.sub-menu.agg-by`),
      description:
        ({group}) =>
        ({t, print}) =>
          t(`query-builder.count-menu.sub-menu.per-group-tooltip`, {
            dataGroupName: print(group),
          }) + print(HELP_DOCS_INSIGHTS_LINK),
      icon: `info-xs`,
    },
  },
};

export const FunnelsCountSubMenuHeader = {
  [FunnelsCountType.ConversionRate]: {
    [SubSelectionTypes.Aggregation]: {
      label:
        () =>
        ({t}) =>
          t(`query-builder.count-menu.sub-menu.count-method`),
      description:
        () =>
        ({t, print}) =>
          t(`query-builder.count-menu.sub-menu.count-method`) + print(HELP_DOCS_FUNNELS_LINK),
      icon: `info-xs`,
    },
  },
};

const UniqueAggregations: AggregationSelector[] = [
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.unique-aggs.interval`),
    operator: PRIMITIVE_MATH_TYPES.UNIQUE,
    description:
      ({pluralGroup}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.unique-aggs.interval-tooltip`, {dataGroupName: print(pluralGroup)}) +
        print(HELP_DOCS_INSIGHTS_LINK),
  },
  {
    label:
      ({pluralGroupTitle}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.unique-aggs.daily`, {dataGroupName: print(pluralGroupTitle)}),
    operator: XAU_MATH_TYPES.DAU,
    description:
      ({pluralGroup}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.unique-aggs.daily-tooltip`, {dataGroupName: print(pluralGroup)}) +
        print(HELP_DOCS_INSIGHTS_LINK),
  },
  {
    label:
      ({pluralGroupTitle}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.unique-aggs.weekly`, {dataGroupName: print(pluralGroupTitle)}),
    operator: XAU_MATH_TYPES.WAU,
    description:
      ({pluralGroup}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.unique-aggs.weekly-tooltip`, {dataGroupName: print(pluralGroup)}) +
        print(HELP_DOCS_INSIGHTS_LINK),
  },
  {
    label:
      ({pluralGroupTitle}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.unique-aggs.monthly`, {dataGroupName: print(pluralGroupTitle)}),
    operator: XAU_MATH_TYPES.MAU,
    description:
      ({pluralGroup}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.unique-aggs.monthly-tooltip`, {dataGroupName: print(pluralGroup)}) +
        print(HELP_DOCS_INSIGHTS_LINK),
  },
];

const FrequencyPerUniqueAggregations: AggregationSelector[] = [
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.distribution`),
    operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
    perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
    description:
      ({group, queryOrigin}) =>
      ({t, print}) => {
        if (queryOrigin === QueryOrigin.Canvas) {
          return t(`query-builder.count-menu.operators.disabled-tooltip`, {queryOrigin});
        }
        return (
          t(`query-builder.count-menu.operators.distribution-tooltip`, {dataGroupName: print(group)}) +
          print(HELP_DOCS_INSIGHTS_LINK)
        );
      },
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.avg`),
    operator: PRIMITIVE_MATH_TYPES.AVERAGE,
    perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
    description:
      ({group}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.operators.avg-tooltip`, {dataGroupName: print(group)}) +
        print(HELP_DOCS_INSIGHTS_LINK),
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.median`),
    operator: PRIMITIVE_MATH_TYPES.MEDIAN,
    perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
    description:
      ({group}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.operators.median-tooltip`, {dataGroupName: print(group)}) +
        print(HELP_DOCS_INSIGHTS_LINK),
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.p`),
    subSections: [
      {
        label:
          () =>
          ({t}) =>
            t(`query-builder.count-menu.operators.p25`),
        shortLabel: () => `P25`,
        operator: NUMERIC_MATH_TYPES.P25,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description:
          ({group}) =>
          ({t, print}) =>
            t(`query-builder.count-menu.operators.p25-tooltip`, {dataGroupName: print(group)}) +
            print(HELP_DOCS_INSIGHTS_LINK),
      },
      {
        label:
          () =>
          ({t}) =>
            t(`query-builder.count-menu.operators.p75`),
        shortLabel: () => `P75`,
        operator: NUMERIC_MATH_TYPES.P75,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description:
          ({group}) =>
          ({t, print}) =>
            t(`query-builder.count-menu.operators.p75-tooltip`, {dataGroupName: print(group)}) +
            print(HELP_DOCS_INSIGHTS_LINK),
      },
      {
        label:
          () =>
          ({t}) =>
            t(`query-builder.count-menu.operators.p90`),
        shortLabel: () => `P90`,
        operator: NUMERIC_MATH_TYPES.P90,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description:
          ({group}) =>
          ({t, print}) =>
            t(`query-builder.count-menu.operators.p90-tooltip`, {dataGroupName: print(group)}) +
            print(HELP_DOCS_INSIGHTS_LINK),
      },
      {
        label:
          () =>
          ({t}) =>
            t(`query-builder.count-menu.operators.custom-percentile`),
        operator: PRIMITIVE_MATH_TYPES.CUSTOM_PERCENTILE,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        shortLabel:
          ({percentile}) =>
          ({t}) =>
            percentile !== undefined
              ? t(`query-builder.count-menu.operators.custom-percentile-short`, {percentile})
              : `Nth %`,
        description:
          ({group}) =>
          ({t, print}) =>
            t(`query-builder.count-menu.operators.custom-percentile-tooltip`, {dataGroupName: print(group)}) +
            print(HELP_DOCS_INSIGHTS_LINK),
        requiredModalTrigger: openCustomPercentileModal,
      },
    ] as AggregationSelector[],
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.min`),
    operator: PRIMITIVE_MATH_TYPES.MIN,
    perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
    description:
      ({group}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.operators.min-tooltip`, {dataGroupName: print(group)}) +
        print(HELP_DOCS_INSIGHTS_LINK),
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.max`),
    operator: PRIMITIVE_MATH_TYPES.MAX,
    perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
    description:
      ({group}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.operators.max-tooltip`, {dataGroupName: print(group)}) +
        print(HELP_DOCS_INSIGHTS_LINK),
  },
];

const PropertyAggregations: AggregationSelector[] = [
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.sum`),
    operator: PRIMITIVE_MATH_TYPES.TOTAL,
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.operators.sum-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.avg`),
    operator: PRIMITIVE_MATH_TYPES.AVERAGE,
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.operators.avg-agg-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.median`),
    operator: PRIMITIVE_MATH_TYPES.MEDIAN,
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.operators.median-agg-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.distinct`),
    operator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.operators.distinct-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.p`),
    subSections: [
      {
        label:
          () =>
          ({t}) =>
            t(`query-builder.count-menu.operators.p25`),
        operator: PRIMITIVE_MATH_TYPES.P25,
        shortLabel: () => `P25`,
        description:
          () =>
          ({t, print}) =>
            t(`query-builder.count-menu.operators.p25-agg-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
      },
      {
        label:
          () =>
          ({t}) =>
            t(`query-builder.count-menu.operators.p75`),
        operator: PRIMITIVE_MATH_TYPES.P75,
        shortLabel: () => `P75`,
        description:
          () =>
          ({t, print}) =>
            t(`query-builder.count-menu.operators.p75-agg-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
      },
      {
        label:
          () =>
          ({t}) =>
            t(`query-builder.count-menu.operators.p90`),
        operator: PRIMITIVE_MATH_TYPES.P90,
        shortLabel: () => `P90`,
        description:
          () =>
          ({t, print}) =>
            t(`query-builder.count-menu.operators.p90-agg-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
      },
      {
        label:
          () =>
          ({t}) =>
            t(`query-builder.count-menu.operators.p99`),
        operator: PRIMITIVE_MATH_TYPES.P99,
        shortLabel: () => `P99`,
        description:
          () =>
          ({t, print}) =>
            t(`query-builder.count-menu.operators.p99-agg-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
      },
      {
        label:
          () =>
          ({t}) =>
            t(`query-builder.count-menu.operators.custom-percentile`),
        operator: PRIMITIVE_MATH_TYPES.CUSTOM_PERCENTILE,
        shortLabel:
          ({percentile}) =>
          ({t}) =>
            percentile !== undefined
              ? t(`query-builder.count-menu.operators.custom-percentile-short`, {percentile})
              : `Nth %`,
        description:
          () =>
          ({t, print}) =>
            t(`query-builder.count-menu.operators.custom-percentile-agg-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
        requiredModalTrigger: openCustomPercentileModal,
      },
    ] as AggregationSelector[],
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.min`),
    operator: PRIMITIVE_MATH_TYPES.MIN,
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.operators.min-agg-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.max`),
    operator: PRIMITIVE_MATH_TYPES.MAX,
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.operators.max-agg-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
  },
];

const TTCPropertyAggregations: AggregationSelector[] = [
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.avg`),
    operator: PRIMITIVE_MATH_TYPES.AVERAGE,
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.operators.avg-agg-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.median`),
    operator: PRIMITIVE_MATH_TYPES.MEDIAN,
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.operators.median-agg-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.p`),
    subSections: [
      {
        label:
          () =>
          ({t}) =>
            t(`query-builder.count-menu.operators.p25`),
        operator: PRIMITIVE_MATH_TYPES.P25,
        shortLabel: () => `P25`,
        description:
          () =>
          ({t, print}) =>
            t(`query-builder.count-menu.operators.p25-agg-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
      },
      {
        label:
          () =>
          ({t}) =>
            t(`query-builder.count-menu.operators.p75`),
        operator: PRIMITIVE_MATH_TYPES.P75,
        shortLabel: () => `P75`,
        description:
          () =>
          ({t, print}) =>
            t(`query-builder.count-menu.operators.p75-agg-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
      },
      {
        label:
          () =>
          ({t}) =>
            t(`query-builder.count-menu.operators.p90`),
        operator: PRIMITIVE_MATH_TYPES.P90,
        shortLabel: () => `P90`,
        description:
          () =>
          ({t, print}) =>
            t(`query-builder.count-menu.operators.p90-agg-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
      },
      {
        label:
          () =>
          ({t}) =>
            t(`query-builder.count-menu.operators.p99`),
        operator: PRIMITIVE_MATH_TYPES.P99,
        shortLabel: () => `P99`,
        description:
          () =>
          ({t, print}) =>
            t(`query-builder.count-menu.operators.p99-agg-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
      },
      {
        label:
          () =>
          ({t}) =>
            t(`query-builder.count-menu.operators.custom-percentile`),
        operator: PRIMITIVE_MATH_TYPES.CUSTOM_PERCENTILE,
        shortLabel:
          ({percentile}) =>
          ({t}) =>
            percentile !== undefined
              ? t(`query-builder.count-menu.operators.custom-percentile-short`, {percentile})
              : `Nth %`,
        description:
          () =>
          ({t, print}) =>
            t(`query-builder.count-menu.operators.custom-percentile-agg-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
        requiredModalTrigger: openCustomPercentileModal,
      },
    ] as AggregationSelector[],
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.min`),
    operator: PRIMITIVE_MATH_TYPES.MIN,
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.operators.min-agg-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.max`),
    operator: PRIMITIVE_MATH_TYPES.MAX,
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.operators.max-agg-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
  },
];

const PerUserSum = {
  label:
    () =>
    ({t}) =>
      t(`query-builder.count-menu.operators.sum`),
  perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
  description:
    ({group}) =>
    ({t, print}) =>
      t(`query-builder.count-menu.operators.sum-group-tooltip`, {dataGroupName: print(group)}) +
      print(HELP_DOCS_INSIGHTS_LINK),
};
const PerUserAverage = {
  label:
    () =>
    ({t}) =>
      t(`query-builder.count-menu.operators.avg`),
  perUserOperator: PER_USER_AGGREGATIONS.AVERAGE,
  description:
    ({group}) =>
    ({t, print}) =>
      t(`query-builder.count-menu.operators.avg-group-tooltip`, {dataGroupName: print(group)}) +
      print(HELP_DOCS_INSIGHTS_LINK),
};
const PerUserDistinctCount = {
  label:
    () =>
    ({t}) =>
      t(`query-builder.count-menu.operators.distinct`),
  perUserOperator: PER_USER_AGGREGATIONS.UNIQUE_VALUES,
  description:
    ({group}) =>
    ({t, print}) =>
      t(`query-builder.count-menu.operators.distinct-group-tooltip`, {dataGroupName: print(group)}) +
      print(HELP_DOCS_INSIGHTS_LINK),
};
const PerUserMinimum = {
  label:
    () =>
    ({t}) =>
      t(`query-builder.count-menu.operators.min`),
  perUserOperator: PER_USER_AGGREGATIONS.MIN,
  description:
    ({group}) =>
    ({t, print}) =>
      t(`query-builder.count-menu.operators.min-group-tooltip`, {dataGroupName: print(group)}) +
      print(HELP_DOCS_INSIGHTS_LINK),
};
const PerUserMaximum = {
  label:
    () =>
    ({t}) =>
      t(`query-builder.count-menu.operators.max`),
  perUserOperator: PER_USER_AGGREGATIONS.MAX,
  description:
    ({group}) =>
    ({t, print}) =>
      t(`query-builder.count-menu.operators.max-group-tooltip`, {dataGroupName: print(group)}) +
      print(HELP_DOCS_INSIGHTS_LINK),
};

const PropertyPerUniqueAggregations: AggregationSelector[] = [
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.avg`),
    operator: PRIMITIVE_MATH_TYPES.AVERAGE,
    description:
      ({group}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.operators.avg-count-tooltip`, {dataGroupName: print(group)}) +
        print(HELP_DOCS_INSIGHTS_LINK),
    subAggregations: [PerUserSum, PerUserAverage, PerUserDistinctCount, PerUserMinimum, PerUserMaximum].map(
      (aggregator) => ({
        ...aggregator,
        operator: PRIMITIVE_MATH_TYPES.AVERAGE,
      }),
    ),
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.sum`),
    operator: PRIMITIVE_MATH_TYPES.TOTAL,
    description:
      ({group}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.operators.sum-count-tooltip`, {dataGroupName: print(group)}) +
        print(HELP_DOCS_INSIGHTS_LINK),
    subAggregations: [PerUserAverage, PerUserDistinctCount, PerUserMinimum, PerUserMaximum].map((aggregator) => ({
      ...aggregator,
      operator: PRIMITIVE_MATH_TYPES.TOTAL,
    })),
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.distribution`),
    operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
    description:
      ({pluralGroup, queryOrigin}) =>
      ({t, print}) => {
        if (queryOrigin === QueryOrigin.Canvas) {
          return t(`query-builder.count-menu.operators.disabled-tooltip`, {queryOrigin});
        }
        return (
          t(`query-builder.count-menu.operators.distribution-count-tooltip`, {dataGroupName: print(pluralGroup)}) +
          print(HELP_DOCS_INSIGHTS_LINK)
        );
      },
    subAggregations: [PerUserSum, PerUserAverage, PerUserDistinctCount, PerUserMinimum, PerUserMaximum].map(
      (aggregator) => ({
        ...aggregator,
        operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
      }),
    ),
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.median`),
    operator: PRIMITIVE_MATH_TYPES.MEDIAN,
    description:
      ({group}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.operators.median-count-tooltip`, {dataGroupName: print(group)}) +
        print(HELP_DOCS_INSIGHTS_LINK),
    subAggregations: [PerUserSum, PerUserAverage, PerUserDistinctCount, PerUserMinimum, PerUserMaximum].map(
      (aggregator) => ({
        ...aggregator,
        operator: PRIMITIVE_MATH_TYPES.MEDIAN,
      }),
    ),
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.p`),
    subSections: [
      {
        label:
          () =>
          ({t}) =>
            t(`query-builder.count-menu.operators.p25`),
        operator: PRIMITIVE_MATH_TYPES.P25,
        shortLabel: () => `P25`,
        description:
          ({group}) =>
          ({t, print}) =>
            t(`query-builder.count-menu.operators.p25-count-tooltip`, {dataGroupName: print(group)}) +
            print(HELP_DOCS_INSIGHTS_LINK),
        subAggregations: [PerUserSum, PerUserAverage, PerUserDistinctCount, PerUserMinimum, PerUserMaximum].map(
          (aggregator) => ({
            ...aggregator,
            operator: PRIMITIVE_MATH_TYPES.P25,
          }),
        ),
      },
      {
        label:
          () =>
          ({t}) =>
            t(`query-builder.count-menu.operators.p75`),
        operator: PRIMITIVE_MATH_TYPES.P75,
        shortLabel: () => `P75`,
        description:
          ({group}) =>
          ({t, print}) =>
            t(`query-builder.count-menu.operators.p75-count-tooltip`, {dataGroupName: print(group)}) +
            print(HELP_DOCS_INSIGHTS_LINK),
        subAggregations: [PerUserSum, PerUserAverage, PerUserDistinctCount, PerUserMinimum, PerUserMaximum].map(
          (aggregator) => ({
            ...aggregator,
            operator: PRIMITIVE_MATH_TYPES.P75,
          }),
        ),
      },
      {
        label:
          () =>
          ({t}) =>
            t(`query-builder.count-menu.operators.p90`),
        operator: PRIMITIVE_MATH_TYPES.P90,
        shortLabel: () => `P90`,
        description:
          ({group}) =>
          ({t, print}) =>
            t(`query-builder.count-menu.operators.p90-count-tooltip`, {dataGroupName: print(group)}) +
            print(HELP_DOCS_INSIGHTS_LINK),
        subAggregations: [PerUserSum, PerUserAverage, PerUserDistinctCount, PerUserMinimum, PerUserMaximum].map(
          (aggregator) => ({
            ...aggregator,
            operator: PRIMITIVE_MATH_TYPES.P90,
          }),
        ),
      },
      {
        label:
          () =>
          ({t}) =>
            t(`query-builder.count-menu.operators.custom-percentile`),
        operator: PRIMITIVE_MATH_TYPES.CUSTOM_PERCENTILE,
        shortLabel:
          ({percentile}) =>
          ({t}) =>
            percentile !== undefined
              ? t(`query-builder.count-menu.operators.custom-percentile-short`, {percentile})
              : `Nth %`,
        description:
          ({group}) =>
          ({t, print}) =>
            t(`query-builder.count-menu.operators.custom-percentile-tooltip`, {dataGroupName: print(group)}) +
            print(HELP_DOCS_INSIGHTS_LINK),
        requiredModalTrigger: openCustomPercentileModal,
        subAggregations: [PerUserSum, PerUserAverage, PerUserDistinctCount, PerUserMinimum, PerUserMaximum].map(
          (aggregator) => ({
            ...aggregator,
            operator: PRIMITIVE_MATH_TYPES.CUSTOM_PERCENTILE,
          }),
        ),
      },
    ] as AggregationSelector[],
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.min`),
    operator: PRIMITIVE_MATH_TYPES.MIN,
    description:
      ({group}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.operators.min-count-tooltip`, {dataGroupName: print(group)}) +
        print(HELP_DOCS_INSIGHTS_LINK),
    subAggregations: [PerUserSum, PerUserAverage, PerUserDistinctCount, PerUserMaximum].map((aggregator) => ({
      ...aggregator,
      operator: PRIMITIVE_MATH_TYPES.MIN,
    })),
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.operators.max`),
    operator: PRIMITIVE_MATH_TYPES.MAX,
    description:
      ({group}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.operators.max-count-tooltip`, {dataGroupName: print(group)}) +
        print(HELP_DOCS_INSIGHTS_LINK),
    subAggregations: [PerUserSum, PerUserAverage, PerUserDistinctCount, PerUserMinimum].map((aggregator) => ({
      ...aggregator,
      operator: PRIMITIVE_MATH_TYPES.MAX,
    })),
  },
];

const ConversionRatesAggregations: AggregationSelector[] = [
  {
    label:
      () =>
      ({t}) =>
        `${t(`query-builder.count-menu.conversion-aggs.uniques`)} (${t(`query-builder.default`)})`,
    operator: FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE,
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.conversion-aggs.uniques-tooltip`) + print(HELP_DOCS_FUNNELS_LINK),
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.conversion-aggs.totals`),
    operator: FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL,
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.conversion-aggs.totals-tooltip`) + print(HELP_DOCS_FUNNELS_LINK),
  },
  {
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.conversion-aggs.sessions`),
    operator: FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION,
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.conversion-aggs.sessions-tooltip`) + print(HELP_DOCS_FUNNELS_LINK),
  },
];

const needsNestedProperty = ({property}: {property: PropertySectionItem}): boolean =>
  property && isListOfObjectsSegment(propertyItemToSegmentation(property, property?.resourceType));

const needsRevenueProperty = (property: PropertySectionItem): property is SectionItemProperty =>
  property && isProperty(property) && property.value === RECURRING_REVENUE_CHANGE.value;

const hasRequiredPropertyValues = ({
  property,
  nestedProperty,
}: {
  property: PropertySectionItem;
  nestedProperty: Segmentation;
}) => {
  if (needsNestedProperty({property})) {
    return !!nestedProperty && !!property;
  }
  if (needsRevenueProperty(property)) {
    return !!property.behavior;
  }
  return !!property;
};

export const PROFILE_COUNT_MENU_ITEMS: CountTypeSelector[] = [
  {
    countType: InsightsCountType.Total,
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.total-profiles`),
    operator: PRIMITIVE_MATH_TYPES.TOTAL,
    description:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.total-profiles-tooltip`),
    hasRequiredSelections: () => true,
    canExpandSubSelections: () => false,
  },
  {
    countType: InsightsCountType.PropertyValues,
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.agg-prop`),
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.agg-prop-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
    aggregations: PropertyAggregations,
    forProperty: true,
    requiredSubSelections: ({property}) =>
      [
        SubSelectionTypes.Aggregation,
        SubSelectionTypes.Property,
        needsNestedProperty({property}) ? SubSelectionTypes.NestedProperty : null,
        needsRevenueProperty(property) ? SubSelectionTypes.RevenueProperty : null,
      ].filter(Boolean),
    hasRequiredSelections: ({property, aggregation, nestedProperty}) =>
      hasRequiredPropertyValues({property, nestedProperty}) && !!aggregation,
    canExpandSubSelections: ({countType}) => !!countType,
  },
];

export const INSIGHTS_COUNT_MENU_ITEMS: CountTypeSelector[] = [
  {
    countType: InsightsCountType.UniqueUsers,
    label:
      ({pluralGroupTitle}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.unique-dg`, {dataGroupName: print(pluralGroupTitle)}),
    operator: PRIMITIVE_MATH_TYPES.UNIQUE,
    description:
      ({pluralGroup}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.unique-dg-tooltip`, {dataGroupName: print(pluralGroup)}) +
        print(HELP_DOCS_INSIGHTS_LINK),
    expanderIcon: `settings-small`,
    aggregations: [
      ...UniqueAggregations,
      {
        label:
          ({pluralGroupTitle}) =>
          ({t, print}) =>
            t(`query-builder.count-menu.unique-aggs.cumulative`, {dataGroupName: print(pluralGroupTitle)}),
        operator: PRIMITIVE_MATH_TYPES.CUMULATIVE_UNIQUE,
        description:
          ({pluralGroup}) =>
          ({t, print}) =>
            t(`query-builder.count-menu.unique-aggs.cumulative-tooltip`, {dataGroupName: print(pluralGroup)}) +
            print(HELP_DOCS_INSIGHTS_LINK),
      },
    ],
    requiredSubSelections: [SubSelectionTypes.Aggregation, SubSelectionTypes.CountUsersOnce],
    hasRequiredSelections: ({aggregation, countUsersOnceType}) => Boolean(aggregation || countUsersOnceType),
    /** can expand sub selections only is an operator is XAU and count type is selected */
    canExpandSubSelections: ({countType, aggregation, countUsersOnceType}) =>
      Boolean(countUsersOnceType) ||
      (countType &&
        [XAU_MATH_TYPES.DAU, XAU_MATH_TYPES.WAU, XAU_MATH_TYPES.MAU, PRIMITIVE_MATH_TYPES.CUMULATIVE_UNIQUE].includes(
          aggregation as XAU_MATH_TYPES | PRIMITIVE_MATH_TYPES,
        )),
  },
  {
    countType: InsightsCountType.Total,
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.total-events`),
    operator: PRIMITIVE_MATH_TYPES.TOTAL,
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.total-events-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
    hasRequiredSelections: () => true,
    canExpandSubSelections: () => false,
  },
  {
    countType: InsightsCountType.TotalSessions,
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.total-sessions`),
    operator: PRIMITIVE_MATH_TYPES.SESSIONS,
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.total-sessions-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
    hasRequiredSelections: () => true,
    canExpandSubSelections: () => false,
  },
  {
    countType: InsightsCountType.FrequencyPerUser,
    label:
      ({groupTitle}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.frequency-per-dg`, {
          dataGroupName: print(groupTitle),
        }),
    description:
      ({group}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.frequency-per-dg-tooltip`, {
          dataGroupName: print(group),
        }) + print(HELP_DOCS_INSIGHTS_LINK),
    aggregations: FrequencyPerUniqueAggregations,
    requiredSubSelections: [SubSelectionTypes.Aggregation],
    hasRequiredSelections: ({aggregation}) => !!aggregation,
    canExpandSubSelections: ({countType}) => !!countType,
  },
  {
    countType: InsightsCountType.PropertyValues,
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.agg-prop`),
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.agg-prop-tooltip`) + print(HELP_DOCS_INSIGHTS_LINK),
    aggregations: PropertyAggregations,
    forProperty: true,
    requiredSubSelections: ({property}) =>
      [
        SubSelectionTypes.Aggregation,
        SubSelectionTypes.Property,
        needsNestedProperty({property}) ? SubSelectionTypes.NestedProperty : null,
      ].filter(Boolean),
    hasRequiredSelections: ({property, aggregation, nestedProperty}) =>
      hasRequiredPropertyValues({property, nestedProperty}) && !!aggregation,
    canExpandSubSelections: ({countType}) => !!countType,
  },
  {
    countType: InsightsCountType.PropertyValuePerUser,
    label:
      ({groupTitle}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.agg-prop-per-dg`, {
          dataGroupName: print(groupTitle),
        }),
    description:
      ({group}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.agg-prop-group-tooltip`, {
          dataGroupName: print(group),
        }) + print(HELP_DOCS_INSIGHTS_LINK),
    aggregations: PropertyPerUniqueAggregations,
    forProperty: true,
    requiredSubSelections: ({property}) =>
      [
        SubSelectionTypes.Aggregation,
        SubSelectionTypes.AggregationPerUser,
        SubSelectionTypes.Property,
        needsNestedProperty({property}) ? SubSelectionTypes.NestedProperty : null,
      ].filter(Boolean),
    hasRequiredSelections: ({nestedProperty, property, aggregationPerUser, aggregation}) =>
      hasRequiredPropertyValues({property, nestedProperty}) && !!property && !!aggregationPerUser && !!aggregation,
    canExpandSubSelections: ({countType}) => !!countType,
  },
];

export const RETENTION_COUNT_MENU_ITEMS: (
  isCountUsersOnceEnabled: boolean,
  isPropertySumEnabled: boolean,
) => CountTypeSelector[] = (isCountUsersOnceEnabled: boolean, isPropertySumEnabled: boolean) =>
  Array<CountTypeSelector>(
    {
      countType: RetentionCountType.RetentionRate,
      operator: RETENTION_MATH_TYPES.RETENTION_RATE,
      label:
        () =>
        ({t}) =>
          t(`query-builder.count-menu.retention-rate`),
      description:
        () =>
        ({t, print}) =>
          t(`query-builder.count-menu.retention-rate-tooltip`) + print(HELP_DOCS_RETENTION_LINK),
      hasRequiredSelections: () => true,
      canExpandSubSelections: () => false,
    },
    {
      countType: RetentionCountType.Unique,
      operator: RETENTION_MATH_TYPES.UNIQUE,
      label:
        ({pluralGroupTitle}) =>
        ({t, print}) =>
          t(`query-builder.count-menu.unique-dg`, {
            dataGroupName: print(pluralGroupTitle),
          }),
      description:
        ({pluralGroupTitle}) =>
        ({t, print}) =>
          t(`query-builder.count-menu.unique-dg-retention-tooltip`, {
            dataGroupName: print(pluralGroupTitle),
          }) + print(HELP_DOCS_RETENTION_LINK),
      expanderIcon: isCountUsersOnceEnabled ? `settings-small` : null,
      requiredSubSelections: isCountUsersOnceEnabled ? [SubSelectionTypes.CountUsersOnce] : null,
      hasRequiredSelections: () => true,
      canExpandSubSelections: ({countUsersOnceType}) => (isCountUsersOnceEnabled ? Boolean(countUsersOnceType) : false),
    },
  ).concat(
    isPropertySumEnabled
      ? [
          {
            countType: RetentionCountType.PropertySum,
            operator: RETENTION_MATH_TYPES.PROPERTY_SUM,
            label:
              () =>
              ({t}) =>
                t(`query-builder.count-menu.property-sum`),
            description:
              () =>
              ({t}) =>
                t(`query-builder.count-menu.property-sum-tooltip`),
            forProperty: true,
            requiredSubSelections: ({property}) =>
              [
                SubSelectionTypes.Property,
                needsNestedProperty({property}) ? SubSelectionTypes.NestedProperty : null,
              ].filter(Boolean),
            hasRequiredSelections: ({property, nestedProperty}) =>
              hasRequiredPropertyValues({property, nestedProperty}),
            canExpandSubSelections: ({countType}) => !!countType,
          },
          {
            countType: RetentionCountType.PropertyAverage,
            operator: RETENTION_MATH_TYPES.PROPERTY_AVERAGE,
            label:
              () =>
              ({t}) =>
                t(`query-builder.count-menu.property-avg`),
            description:
              () =>
              ({t}) =>
                t(`query-builder.count-menu.property-avg-tooltip`),
            forProperty: true,
            requiredSubSelections: ({property}) =>
              [
                SubSelectionTypes.Property,
                needsNestedProperty({property}) ? SubSelectionTypes.NestedProperty : null,
              ].filter(Boolean),
            hasRequiredSelections: ({property, nestedProperty}) =>
              hasRequiredPropertyValues({property, nestedProperty}),
            canExpandSubSelections: ({countType}) => !!countType,
          },
        ]
      : [],
  );

// TODO fix the help doc link. will need a new const
export const FUNNELS_COUNT_MENU_ITEMS: (isCountUsersOnceEnabled: boolean) => CountTypeSelector[] = (
  isCountUsersOnceEnabled: boolean,
) => [
  {
    countType: FunnelsCountType.ConversionRate,
    operator: FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE,
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.conversion-rate`),
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.conversion-rate-tooltip`) + print(HELP_DOCS_FUNNELS_LINK),
    expanderIcon: `settings-small`,
    aggregations: ConversionRatesAggregations,
    requiredSubSelections: [SubSelectionTypes.Aggregation],
    hasRequiredSelections: ({aggregation}) => !!aggregation,
    canExpandSubSelections: ({countType, aggregation}) =>
      countType &&
      [FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL, FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION].includes(
        aggregation as FUNNELS_MATH_TYPES,
      ),
  },
  {
    countType: FunnelsCountType.Unique,
    operator: FUNNELS_MATH_TYPES.UNIQUE,
    label:
      ({pluralGroupTitle}) =>
      ({t, print}) =>
        t(`query-builder.count-menu.unique-dg`, {
          dataGroupName: print(pluralGroupTitle),
        }),
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.unique-dg-funnels-tooltip`) + print(HELP_DOCS_FUNNELS_LINK),
    expanderIcon: isCountUsersOnceEnabled ? `settings-small` : null,
    requiredSubSelections: isCountUsersOnceEnabled ? [SubSelectionTypes.CountUsersOnce] : null,
    hasRequiredSelections: () => true,
    canExpandSubSelections: ({countUsersOnceType}) => (isCountUsersOnceEnabled ? Boolean(countUsersOnceType) : false),
  },
  {
    countType: FunnelsCountType.General,
    operator: FUNNELS_MATH_TYPES.GENERAL,
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.total-conversions`),
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.total-conversions-tooltip`) + print(HELP_DOCS_FUNNELS_LINK),
    hasRequiredSelections: () => true,
    canExpandSubSelections: () => false,
  },
  {
    countType: FunnelsCountType.Session,
    operator: FUNNELS_MATH_TYPES.SESSION,
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.total-sessions`),
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.total-sessions-funnels-tooltip`) + print(HELP_DOCS_FUNNELS_LINK),
    hasRequiredSelections: () => true,
    canExpandSubSelections: () => false,
  },
  {
    countType: FunnelsCountType.General,
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.time-to-convert`),
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.time-to-convert-tooltip`) + print(HELP_DOCS_FUNNELS_TTC_NEW_LINK),
    aggregations: TTCPropertyAggregations,
    forProperty: true,
    property: FUNNELS_TIME_TO_CONVERSION,
    requiredSubSelections: () => [SubSelectionTypes.Aggregation],
    hasRequiredSelections: ({aggregation}) => !!aggregation,
    canExpandSubSelections: ({countType}) => !!countType,
  },
  {
    countType: FunnelsCountType.PropertySum,
    operator: FUNNELS_MATH_TYPES.PROPERTY_SUM,
    label:
      () =>
      ({t}) =>
        t(`query-builder.count-menu.property-sum`),
    description:
      () =>
      ({t, print}) =>
        t(`query-builder.count-menu.property-sum-funnels-tooltip`) + print(HELP_DOCS_FUNNELS_PROPERTY_SUM_LINK),
    forProperty: true,
    requiredSubSelections: ({property}) =>
      [SubSelectionTypes.Property, needsNestedProperty({property}) ? SubSelectionTypes.NestedProperty : null].filter(
        Boolean,
      ),
    hasRequiredSelections: ({property, nestedProperty}) => hasRequiredPropertyValues({property, nestedProperty}),
    canExpandSubSelections: ({countType}) => !!countType,
  },
];

export const DisabledCountTypes: Record<QueryOrigin, MATH_TYPE[]> = {
  canvas: [PRIMITIVE_MATH_TYPES.HISTOGRAM],
  experiment: [PRIMITIVE_MATH_TYPES.HISTOGRAM],
  report: [],
};
